import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Printer, X, FileText, Settings2, Eye, EyeOff,
  GripVertical, RotateCcw, Maximize2, Minimize2,
} from "lucide-react";
import { useState, useEffect, Fragment } from "react";

// ── Company ───────────────────────────────────────────────────────────────────

export interface PrintCompany {
  id?: string;
  name?: string;
  color?: string;
  logoUrl?: string;
  tagline?: string;
  docHeaderStyle?: string;
  address?: string;
  phone?: string;
  contact?: string;
  vatNo?: string;
  crNo?: string;
  website?: string;
  taxLabel?: string;
}

// ── Doc types ─────────────────────────────────────────────────────────────────

export type PrintDocType =
  | "client-quotation" | "client-vo" | "client-descope"
  | "client-invoice"   | "client-receipt"
  | "sc-po"  | "sc-vo"  | "sc-descope" | "sc-invoice"
  | "sp-po"  | "sp-invoice";

interface DocConfig {
  label: string;
  shortLabel: string;
  partyLabel: string;
  direction: "outbound" | "inbound";
}

const DOC_CONFIG: Record<PrintDocType, DocConfig> = {
  "client-quotation": { label: "Commercial Proposal",         shortLabel: "CP",      partyLabel: "Client",        direction: "outbound" },
  "client-vo":        { label: "Variation Order",             shortLabel: "VO",      partyLabel: "Client",        direction: "outbound" },
  "client-descope":   { label: "Descope Order",               shortLabel: "DS",      partyLabel: "Client",        direction: "outbound" },
  "client-invoice":   { label: "Tax Invoice",                 shortLabel: "INV",     partyLabel: "Client",        direction: "outbound" },
  "client-receipt":   { label: "Payment Receipt",             shortLabel: "RCP",     partyLabel: "Client",        direction: "inbound"  },
  "sc-po":            { label: "Subcontract Purchase Order",  shortLabel: "SC-PO",   partyLabel: "Subcontractor", direction: "inbound"  },
  "sc-vo":            { label: "Subcontract Variation Order", shortLabel: "SC-VO",   partyLabel: "Subcontractor", direction: "inbound"  },
  "sc-descope":       { label: "Subcontract Descope Order",   shortLabel: "SC-DS",   partyLabel: "Subcontractor", direction: "inbound"  },
  "sc-invoice":       { label: "Subcontractor Invoice",       shortLabel: "SC-INV",  partyLabel: "Subcontractor", direction: "inbound"  },
  "sp-po":            { label: "Supplier Purchase Order",     shortLabel: "SP-PO",   partyLabel: "Supplier",      direction: "inbound"  },
  "sp-invoice":       { label: "Supplier Invoice",            shortLabel: "SP-INV",  partyLabel: "Supplier",      direction: "inbound"  },
};

// ── Line items ────────────────────────────────────────────────────────────────

type LineItem = {
  description: string;
  note?: string;
  qty?: number | string;
  unit?: string;
  unitPrice?: number | string;
  amount: number | string;
  taxAmount?: number | string;
  category?: string;
};

function normalizeLineItems(raw: unknown): LineItem[] {
  if (!Array.isArray(raw)) return [];
  return (raw as Record<string, unknown>[]).map(item => ({
    description: String(item.description ?? item.desc ?? item.name ?? ""),
    note:        item.note ? String(item.note) : undefined,
    qty:         item.qty as string | number | undefined,
    unit:        item.unit as string | undefined,
    unitPrice:   (item.unitPrice ?? item.rate) as string | number | undefined,
    amount:      (item.amount ?? item.grossAmount ?? 0) as string | number,
    taxAmount:   (item.taxAmount ?? 0) as string | number,
    category:    item.category as string | undefined,
  }));
}

const N   = (v: unknown) => parseFloat(String(v ?? "0")) || 0;
const fc2 = (v: unknown) => N(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Color schemes ─────────────────────────────────────────────────────────────

interface ColorScheme { id: string; label: string; color: string | null; }
const COLOR_SCHEMES: ColorScheme[] = [
  { id: "company", label: "Brand",    color: null      },
  { id: "slate",   label: "Slate",    color: "#334155" },
  { id: "blue",    label: "Blue",     color: "#1d4ed8" },
  { id: "teal",    label: "Teal",     color: "#0d9488" },
  { id: "green",   label: "Green",    color: "#15803d" },
  { id: "orange",  label: "Orange",   color: "#c2410c" },
  { id: "red",     label: "Red",      color: "#b91c1c" },
  { id: "purple",  label: "Purple",   color: "#7c3aed" },
  { id: "black",   label: "Black",    color: "#111827" },
];

// ── Layout customisation ──────────────────────────────────────────────────────

export type PrintSectionId = "header" | "party" | "description" | "lineItems" | "totals" | "notes" | "footer";

interface PrintSection { id: PrintSectionId; label: string; visible: boolean; }

const DEFAULT_SECTIONS: PrintSection[] = [
  { id: "header",      label: "Document Header",        visible: true },
  { id: "party",       label: "Party, Project & Dates", visible: true },
  { id: "description", label: "Description",            visible: true },
  { id: "lineItems",   label: "Line Items / Amount",    visible: true },
  { id: "totals",      label: "Financial Summary",      visible: true },
  { id: "notes",       label: "Notes & Terms",          visible: true },
  { id: "footer",      label: "Footer",                 visible: true },
];

type ColKey = "num" | "qty" | "unit" | "unitPrice" | "tax";
const DEFAULT_COLS: Record<ColKey, boolean> = { num: true, qty: true, unit: true, unitPrice: true, tax: true };

const lsKey            = (cid?: string) => `fp-print-layout${cid ? "-" + cid : ""}`;
const lsColKey         = (cid?: string) => `fp-print-cols${cid ? "-" + cid : ""}`;
const lsColorKey       = (cid?: string) => `fp-print-color${cid ? "-" + cid : ""}`;
const lsLogoKey        = (cid?: string) => `fp-print-logo${cid ? "-" + cid : ""}`;
const lsSectionFontKey = (cid?: string) => `fp-print-sfont${cid ? "-" + cid : ""}`;

const FONT_PRESETS = [
  { id: "xs",     label: "XS",     scale: 0.82 },
  { id: "sm",     label: "SM",     scale: 0.91 },
  { id: "md",     label: "MD",     scale: 1.00 },
  { id: "lg",     label: "LG",     scale: 1.10 },
  { id: "xl",     label: "XL",     scale: 1.20 },
];

function loadSections(cid?: string): PrintSection[] {
  try {
    const raw = localStorage.getItem(lsKey(cid));
    if (!raw) return DEFAULT_SECTIONS.map(s => ({ ...s }));
    const saved: { id: PrintSectionId; visible: boolean }[] = JSON.parse(raw);
    const visMap   = new Map(saved.map(s => [s.id, s.visible]));
    const orderMap = new Map(saved.map((s, i) => [s.id, i]));
    return [...DEFAULT_SECTIONS]
      .sort((a, b) => (orderMap.get(a.id) ?? 999) - (orderMap.get(b.id) ?? 999))
      .map(s => ({ ...s, visible: visMap.get(s.id) ?? true }));
  } catch { return DEFAULT_SECTIONS.map(s => ({ ...s })); }
}

function saveSections(cid: string | undefined, secs: PrintSection[]) {
  localStorage.setItem(lsKey(cid), JSON.stringify(secs.map(s => ({ id: s.id, visible: s.visible }))));
}

function loadCols(cid?: string): Record<ColKey, boolean> {
  try {
    const raw = localStorage.getItem(lsColKey(cid));
    return raw ? { ...DEFAULT_COLS, ...JSON.parse(raw) } : { ...DEFAULT_COLS };
  } catch { return { ...DEFAULT_COLS }; }
}

function loadSectionFonts(cid?: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(lsSectionFontKey(cid));
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface DocPrintViewProps {
  open: boolean;
  onClose: () => void;
  printDocType: PrintDocType;
  doc: Record<string, unknown>;
  company: PrintCompany | null | undefined;
  project: { name: string; address?: string };
  currency?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function DocPrintView({
  open, onClose, printDocType, doc, company, project, currency = "USD",
}: DocPrintViewProps) {

  const config = DOC_CONFIG[printDocType];

  // Computed doc values
  const lineItems    = normalizeLineItems(doc.lineItems);
  const hasLineItems = lineItems.length > 0;
  const hasAnyTax    = lineItems.some(i => N(i.taxAmount) > 0);

  const color       = company?.color || "#1e293b";
  const logoUrl     = company?.logoUrl || "";
  const tagline     = company?.tagline || "";
  const headerStyle = company?.docHeaderStyle || "standard";
  const isMinimal   = headerStyle === "minimal";
  const isBranded   = headerStyle === "branded";

  const gross = hasLineItems
    ? lineItems.reduce((s, i) => s + N(i.amount), 0)
    : N(doc.gross ?? doc.grossAmount ?? doc.contractedValue ?? doc.amount ?? 0);

  const vatRate    = N(doc.vatRate ?? 0);
  const retPct     = N(doc.retPct ?? 0);
  const retAmt     = retPct > 0 ? gross * retPct : 0;
  const advRecPct  = N(doc.advRecPct ?? 0);
  const advAmt     = advRecPct > 0 ? gross * advRecPct : 0; // invoice-level advance → before tax
  const cnAmt      = N(doc.cnAmt ?? 0);

  const rawDeds: Record<string, unknown>[] = Array.isArray(doc.deds) ? (doc.deds as Record<string, unknown>[]) : [];

  // Split deductions into pre-tax (reduce taxable base) vs post-tax (after gross total)
  const preTaxDeds  = rawDeds.filter(d => d.beforeTax === true);
  const postTaxDeds = rawDeds.filter(d => d.beforeTax !== true);
  const preTaxDedsTotal  = preTaxDeds.reduce((s, d) => s + N(d.amount), 0);
  const postTaxDedsTotal = postTaxDeds.reduce((s, d) => s + N(d.amount), 0);

  // Taxable base = gross minus invoice-level advance and pre-tax custom deductions
  const taxableBase = gross - advAmt - preTaxDedsTotal;

  // VAT: when pre-tax deductions exist and vatRate is known, recalculate on taxable base
  const vatAmt = (() => {
    if (hasLineItems) {
      // Pre-tax deductions reduce the taxable base → recalculate VAT if rate is available
      if ((advAmt > 0 || preTaxDedsTotal > 0) && vatRate > 0) return taxableBase * vatRate;
      return lineItems.reduce((s, i) => s + N(i.taxAmount), 0);
    }
    return vatRate > 0 ? taxableBase * vatRate : N(doc.vatAmount ?? 0);
  })();

  const vatPct = (vatRate * 100).toFixed(1);

  // Gross incl tax = taxable base + VAT  (advance already removed from taxable base)
  const grossInclTax = taxableBase + vatAmt;

  const hasPre         = advAmt > 0 || preTaxDeds.length > 0;
  const hasPost        = retAmt > 0 || cnAmt > 0 || postTaxDeds.length > 0;
  const hasDeductions  = hasPre || hasPost;

  const isReceipt = printDocType === "client-receipt";
  const netDue    = isReceipt
    ? N(doc.amount)
    : grossInclTax - retAmt - cnAmt - postTaxDedsTotal;

  const partyName  = String(doc.customerName ?? doc.vendorName ?? doc.scName ?? doc.suppName ?? "—");
  const partyPoRef = String(doc.customerPoNumber ?? doc.supplierQtNumber ?? "");
  const refNumber  = String(doc.refNumber || doc.id || "—");
  const docDate    = String(doc.date || "");
  const status     = String(doc.status || "");
  const validUntil = String(doc.validUntil ?? doc.dueDate ?? "");
  const description = String(doc.description ?? doc.desc ?? "");
  const notes      = String(doc.notes ?? "");
  const isOutbound = config.direction === "outbound";
  const taxLabel   = company?.taxLabel || "VAT";

  // Status badge colours
  const statusPalette =
    status === "Accepted" || status === "Issued"   ? { bg: "#dcfce7", fg: "#166534", border: "#bbf7d0" } :
    status === "Rejected" || status === "Cancelled" ? { bg: "#fee2e2", fg: "#991b1b", border: "#fecaca" } :
    status === "Draft"                               ? { bg: "#fef3c7", fg: "#92400e", border: "#fde68a" } :
                                                      { bg: "#f3f4f6", fg: "#374151", border: "#e5e7eb" };

  // ── Wide / layout state ──────────────────────────────────────────────────

  const [wide, setWide] = useState<boolean>(() => {
    try { const v = localStorage.getItem("fp-print-wide"); return v === null ? true : v === "1"; } catch { return true; }
  });
  const toggleWide = () => setWide(w => {
    const next = !w;
    try { localStorage.setItem("fp-print-wide", next ? "1" : "0"); } catch {}
    return next;
  });

  useEffect(() => {
    const sid = "fp-print-page";
    let el = document.getElementById(sid) as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement("style");
      el.id = sid;
      document.head.appendChild(el);
    }
    el.textContent = `@page { size: A4 ${wide ? "landscape" : "portrait"}; margin: 0; }`;
  }, [wide]);

  const DOC_W  = wide ? 1060 : 794;
  const DOC_H  = wide ? 760  : 1123;
  const PAD    = wide ? "32px 52px" : "36px 48px 32px";
  const dialogMaxW = (c: boolean) => wide
    ? (c ? "1400px" : "1200px")
    : (c ? "1040px" : "920px");

  // ── Customize panel state ────────────────────────────────────────────────

  const [customize, setCustomize] = useState(false);
  const [sections,  setSections]  = useState<PrintSection[]>(() => loadSections(company?.id));
  const [dragOver,  setDragOver]  = useState<PrintSectionId | null>(null);

  // Color scheme
  const [colorScheme, setColorScheme] = useState<string>(() => {
    try { return localStorage.getItem(lsColorKey(company?.id)) || "company"; } catch { return "company"; }
  });
  const saveColorScheme = (id: string) => {
    setColorScheme(id);
    try { localStorage.setItem(lsColorKey(company?.id), id); } catch {}
  };

  // Per-section font scales
  const [sectionFonts, setSectionFonts] = useState<Record<string, string>>(() => loadSectionFonts(company?.id));
  const saveSectionFont = (sid: PrintSectionId, presetId: string) => {
    setSectionFonts(prev => {
      const next = { ...prev, [sid]: presetId };
      try { localStorage.setItem(lsSectionFontKey(company?.id), JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const getSectionScale = (sid: PrintSectionId) =>
    FONT_PRESETS.find(p => p.id === (sectionFonts[sid] ?? "md"))?.scale ?? 1.0;

  // Logo visibility
  const [showLogo, setShowLogo] = useState<boolean>(() => {
    try { const v = localStorage.getItem(lsLogoKey(company?.id)); return v === null ? true : v === "1"; } catch { return true; }
  });
  const toggleLogo = () => setShowLogo(v => {
    const next = !v;
    try { localStorage.setItem(lsLogoKey(company?.id), next ? "1" : "0"); } catch {}
    return next;
  });

  // Column visibility for line items
  const [cols, setCols] = useState<Record<ColKey, boolean>>(() => loadCols(company?.id));
  const toggleCol = (k: ColKey) => setCols(prev => {
    const next = { ...prev, [k]: !prev[k] };
    try { localStorage.setItem(lsColKey(company?.id), JSON.stringify(next)); } catch {}
    return next;
  });
  const resetCols = () => setCols(prev => {
    const next = { ...DEFAULT_COLS };
    try { localStorage.setItem(lsColKey(company?.id), JSON.stringify(next)); } catch {}
    return next;
  });

  // Resolved accent color from scheme
  const resolvedAccent = colorScheme === "company"
    ? (isBranded ? color : (company?.color || "#1e293b"))
    : (COLOR_SCHEMES.find(s => s.id === colorScheme)?.color ?? "#1e293b");
  const accentColor = resolvedAccent;

  const updateSections = (secs: PrintSection[]) => { setSections(secs); saveSections(company?.id, secs); };
  const toggleVisible  = (id: PrintSectionId)   => updateSections(sections.map(s => s.id === id ? { ...s, visible: !s.visible } : s));
  const resetLayout    = ()                       => {
    updateSections(DEFAULT_SECTIONS.map(s => ({ ...s }))); resetCols();
    setSectionFonts({}); try { localStorage.removeItem(lsSectionFontKey(company?.id)); } catch {}
  };

  const handleDragOver = (e: React.DragEvent, id: PrintSectionId) => {
    e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOver(id);
  };
  const handleDrop = (e: React.DragEvent, targetId: PrintSectionId) => {
    e.preventDefault();
    const srcId = e.dataTransfer.getData("text/plain") as PrintSectionId;
    if (!srcId || srcId === targetId) { setDragOver(null); return; }
    const from = sections.findIndex(s => s.id === srcId);
    const to   = sections.findIndex(s => s.id === targetId);
    if (from === -1 || to === -1) { setDragOver(null); return; }
    const next = [...sections]; const [m] = next.splice(from, 1); next.splice(to, 0, m);
    updateSections(next); setDragOver(null);
  };

  // ── Column config for line items ─────────────────────────────────────────

  const showNum      = cols.num;
  const showQty      = cols.qty;
  const showUnit     = cols.unit;
  const showUnitPrc  = cols.unitPrice;
  const showTaxCol   = cols.tax && hasAnyTax;

  // Build colgroup widths dynamically based on visible cols
  // Description always fills remaining space
  const fixedCols = [
    showNum     ? 3   : 0,
    showQty     ? 6   : 0,
    showUnit    ? 6   : 0,
    showUnitPrc ? 14  : 0,
    14, // Amount always shown
    showTaxCol  ? 13  : 0,  // wider so numbers (e.g. 51,000.00) don't clip
  ].filter(Boolean);
  const fixedTotal = fixedCols.reduce((s, v) => s + v, 0);
  const descWidth  = Math.max(100 - fixedTotal, 25);

  // ── Section renderers ────────────────────────────────────────────────────

  /* HEADER */
  const S_header = (
    <>
      {isBranded && <div style={{ height: "7px", backgroundColor: accentColor, borderRadius: "0" }} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: PAD, paddingBottom: "0" }}>
        {/* Left — company */}
        <div style={{ flex: 1, paddingRight: "40px" }}>
          {showLogo && logoUrl && (
            isBranded ? (
              <img src={logoUrl} alt="Logo"
                style={{ height: "56px", maxWidth: "200px", objectFit: "contain", marginBottom: "10px", display: "block" }} />
            ) : !isMinimal ? (
              <img src={logoUrl} alt="Logo"
                style={{ height: "40px", maxWidth: "160px", objectFit: "contain", marginBottom: "8px", display: "block" }} />
            ) : null
          )}
          <div style={{ fontWeight: 900, fontSize: "17px", color: isBranded ? accentColor : "#111", letterSpacing: "-0.3px" }}>
            {company?.name || "Company"}
          </div>
          {tagline && !isMinimal && (
            <div style={{ fontSize: "10px", color: "#888", marginTop: "2px", fontStyle: "italic" }}>{tagline}</div>
          )}
          {!isMinimal && (
            <div style={{ marginTop: "8px", lineHeight: "1.7", display: "grid", gridTemplateColumns: "auto 1fr", columnGap: "6px" }}>
              {company?.address && <><span style={{ fontSize: "9px", color: "#aaa", fontWeight: 700, textTransform: "uppercase" }}>Address</span><span style={{ fontSize: "9.5px", color: "#555" }}>{company.address}</span></>}
              {company?.phone   && <><span style={{ fontSize: "9px", color: "#aaa", fontWeight: 700, textTransform: "uppercase" }}>Tel</span>    <span style={{ fontSize: "9.5px", color: "#555" }}>{company.phone}</span></>}
              {company?.contact && <><span style={{ fontSize: "9px", color: "#aaa", fontWeight: 700, textTransform: "uppercase" }}>Email</span>  <span style={{ fontSize: "9.5px", color: "#555" }}>{company.contact}</span></>}
              {company?.website && <><span style={{ fontSize: "9px", color: "#aaa", fontWeight: 700, textTransform: "uppercase" }}>Web</span>    <span style={{ fontSize: "9.5px", color: "#555" }}>{company.website}</span></>}
              {company?.vatNo   && <><span style={{ fontSize: "9px", color: "#aaa", fontWeight: 700, textTransform: "uppercase" }}>{taxLabel} Reg</span><span style={{ fontSize: "9.5px", color: "#555" }}>{company.vatNo}</span></>}
              {company?.crNo    && <><span style={{ fontSize: "9px", color: "#aaa", fontWeight: 700, textTransform: "uppercase" }}>CR No.</span> <span style={{ fontSize: "9.5px", color: "#555" }}>{company.crNo}</span></>}
            </div>
          )}
        </div>
        {/* Right — document identity */}
        <div style={{ textAlign: "right", minWidth: wide ? "280px" : "220px" }}>
          <div style={{
            display: "inline-block",
            backgroundColor: accentColor,
            color: "#fff",
            padding: "6px 16px 6px 20px",
            borderRadius: "4px 0 0 4px",
            marginRight: "-" + (wide ? "52px" : "48px"),
            marginBottom: "10px",
          }}>
            <div style={{ fontSize: "13px", fontWeight: 900, textTransform: "uppercase", letterSpacing: "1.5px" }}>
              {config.label}
            </div>
          </div>
          <div style={{ clear: "both", marginTop: "8px" }}>
            <div style={{ fontSize: "10px", color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Reference</div>
            <div style={{ fontSize: "16px", fontWeight: 800, fontFamily: "monospace", color: "#111", marginTop: "2px" }}>
              #{refNumber}
            </div>
          </div>
          {status && (
            <div style={{
              display: "inline-block", marginTop: "8px",
              padding: "4px 12px", borderRadius: "20px",
              fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px",
              backgroundColor: statusPalette.bg, color: statusPalette.fg,
              border: "1px solid " + statusPalette.border,
            }}>
              {status}
            </div>
          )}
          {docDate && (
            <div style={{ marginTop: "8px" }}>
              <span style={{ fontSize: "9px", color: "#aaa", fontWeight: 700, textTransform: "uppercase" }}>Date </span>
              <span style={{ fontSize: "10px", color: "#555", fontWeight: 600 }}>{docDate}</span>
            </div>
          )}
        </div>
      </div>
      {/* Divider */}
      <div style={{ margin: `16px ${wide ? "52px" : "48px"} 0`, height: "1.5px", background: isBranded ? `linear-gradient(to right, ${accentColor}, transparent)` : "#e5e7eb" }} />
    </>
  );

  /* PARTY + PROJECT + DATES */
  const partyPhone    = String(doc.partyPhone    ?? "");
  const partyEmail    = String(doc.partyEmail    ?? "");
  const partyCountry  = String(doc.partyCountry  ?? "");
  const partyVatNo    = String(doc.partyVatNo    ?? "");
  const partyContact  = String(doc.partyContact  ?? "");
  const partySubtitle = String(doc.partySubtitle ?? "");

  const S_party = (
    <div style={{ padding: `20px ${wide ? "52px" : "48px"} 0`, display: "grid", gridTemplateColumns: wide ? "1fr 1fr" : "1fr 1fr", gap: "16px" }}>
      {/* Bill To */}
      <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", padding: "14px 16px", borderTop: `3px solid ${accentColor}` }}>
        <div style={{ fontSize: "8.5px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: "#888", marginBottom: "8px" }}>
          {isOutbound ? "Bill To" : "Issued To"} — {config.partyLabel}
        </div>
        <div style={{ fontWeight: 800, fontSize: "13px", color: "#111", marginBottom: "4px" }}>{partyName}</div>
        {!!partySubtitle && <div style={{ fontSize: "9.5px", color: "#888", fontStyle: "italic", marginBottom: "6px" }}>{partySubtitle}</div>}
        {partyPoRef && <div style={{ fontSize: "10px", color: "#666", marginBottom: "4px" }}>Your Ref: <strong>{partyPoRef}</strong></div>}
        {(partyPhone || partyEmail || partyCountry || partyVatNo || partyContact) && (
          <div style={{ marginTop: "8px", lineHeight: "1.7", display: "grid", gridTemplateColumns: "auto 1fr", columnGap: "6px" }}>
            {!!partyContact && <><span style={{ fontSize: "8.5px", color: "#aaa", fontWeight: 700, textTransform: "uppercase" }}>Contact</span><span style={{ fontSize: "9.5px", color: "#555" }}>{partyContact}</span></>}
            {!!partyPhone   && <><span style={{ fontSize: "8.5px", color: "#aaa", fontWeight: 700, textTransform: "uppercase" }}>Tel</span>    <span style={{ fontSize: "9.5px", color: "#555" }}>{partyPhone}</span></>}
            {!!partyEmail   && <><span style={{ fontSize: "8.5px", color: "#aaa", fontWeight: 700, textTransform: "uppercase" }}>Email</span>  <span style={{ fontSize: "9.5px", color: "#555" }}>{partyEmail}</span></>}
            {!!partyCountry && <><span style={{ fontSize: "8.5px", color: "#aaa", fontWeight: 700, textTransform: "uppercase" }}>Country</span><span style={{ fontSize: "9.5px", color: "#555" }}>{partyCountry}</span></>}
            {!!partyVatNo   && <><span style={{ fontSize: "8.5px", color: "#aaa", fontWeight: 700, textTransform: "uppercase" }}>{taxLabel} Reg</span><span style={{ fontSize: "9.5px", color: "#555" }}>{partyVatNo}</span></>}
          </div>
        )}
      </div>
      {/* Project + Details merged */}
      <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden", borderTop: `3px solid ${accentColor}` }}>
        {/* Project row */}
        <div style={{ padding: "14px 16px" }}>
          <div style={{ fontSize: "8.5px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: "#888", marginBottom: "6px" }}>Project</div>
          <div style={{ fontWeight: 800, fontSize: "13px", color: "#111", marginBottom: "2px" }}>{project.name}</div>
          {project.address && <div style={{ fontSize: "10px", color: "#666" }}>{project.address}</div>}
        </div>
        {/* Details row */}
        <div style={{ borderTop: "1px solid #f0f0f0", backgroundColor: "#f9fafb", padding: "10px 16px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {docDate    && <MetaRow label="Date"    value={docDate} />}
              {validUntil && <MetaRow label={printDocType === "client-invoice" ? "Due Date" : "Valid Until"} value={validUntil} />}
              {isReceipt && !!doc.method && <MetaRow label="Method"   value={String(doc.method)} />}
              {isReceipt && !!doc.ref    && <MetaRow label="Txn Ref"  value={String(doc.ref)} />}
              {isReceipt && !!doc.tp     && <MetaRow label="Type"     value={String(doc.tp)} />}
              {!!doc.invoiceType          && <MetaRow label="Type"     value={String(doc.invoiceType)} />}
              {!!doc.cat                  && <MetaRow label="Category" value={String(doc.cat)} />}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  /* DESCRIPTION */
  const S_description = description ? (
    <div style={{ padding: `16px ${wide ? "52px" : "48px"} 0` }}>
      <div style={{ fontSize: "8.5px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: "#888", marginBottom: "6px" }}>Description</div>
      <div style={{ fontSize: "11px", color: "#444", lineHeight: "1.6" }}>{description}</div>
    </div>
  ) : null;

  /* LINE ITEMS TABLE */
  const S_lineItems = (
    <div style={{ padding: `20px ${wide ? "52px" : "48px"} 0` }}>
      {isReceipt && !hasLineItems ? (
        /* Receipt simple block */
        <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
          <div style={{ backgroundColor: accentColor, color: "#fff", padding: "10px 18px", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px" }}>
            Payment Details
          </div>
          <div style={{ padding: "18px 20px" }}>
            {!!doc.invRef && String(doc.invRef) && (
              <div style={{ fontSize: "10px", color: "#555", marginBottom: "8px" }}>
                Invoice Reference: <strong style={{ color: "#111" }}>{String(doc.invRef)}</strong>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "12px", color: "#555" }}>{description || "Payment received"}</span>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "9px", color: "#aaa", fontWeight: 600, textTransform: "uppercase", marginBottom: "2px" }}>Amount Received</div>
                <div style={{ fontSize: "20px", fontWeight: 900, fontFamily: "monospace", color: "#111" }}>{currency} {fc2(doc.amount)}</div>
              </div>
            </div>
          </div>
        </div>
      ) : hasLineItems ? (
        <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <colgroup>
              {showNum     && <col style={{ width: "4%" }}  />}
              <col style={{ width: `${descWidth}%` }} />
              {showQty     && <col style={{ width: "7%" }}  />}
              {showUnit    && <col style={{ width: "7%" }}  />}
              {showUnitPrc && <col style={{ width: "16%" }} />}
              <col style={{ width: "16%" }} />
              {showTaxCol  && <col style={{ width: "10%" }} />}
            </colgroup>
            <thead>
              <tr style={{ backgroundColor: accentColor, color: "#fff" }}>
                {showNum     && <th style={thStyle("center")}>  #          </th>}
                <th style={thStyle("left")}>                    Description </th>
                {showQty     && <th style={thStyle("center")}> Qty         </th>}
                {showUnit    && <th style={thStyle("center")}> Unit        </th>}
                {showUnitPrc && <th style={thStyle("right")}>  Unit Price  </th>}
                <th style={thStyle("right")}>                  Amount      </th>
                {showTaxCol  && <th style={thStyle("right")}>  Tax         </th>}
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0", backgroundColor: idx % 2 === 0 ? "#fff" : "#f9fafb" }}>
                  {showNum && (
                    <td style={{ padding: "9px 10px", textAlign: "center", fontSize: "10px", color: "#bbb", fontWeight: 600 }}>{idx + 1}</td>
                  )}
                  <td style={{ padding: "9px 10px", fontSize: "10px" }}>
                    {item.category && (
                      <div style={{ fontSize: "8px", color: "#bbb", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "2px" }}>
                        {item.category}
                      </div>
                    )}
                    <div style={{ color: "#222", lineHeight: "1.5", fontWeight: 500 }}>{item.description || "—"}</div>
                    {!!item.note && (
                      <div style={{ color: "#888", fontSize: "9px", lineHeight: "1.4", marginTop: "3px", fontStyle: "italic" }}>
                        {item.note}
                      </div>
                    )}
                  </td>
                  {showQty && (
                    <td style={{ padding: "9px 10px", textAlign: "center", fontSize: "10px", color: "#555" }}>{item.qty != null ? item.qty : "—"}</td>
                  )}
                  {showUnit && (
                    <td style={{ padding: "9px 10px", textAlign: "center", fontSize: "10px", color: "#555" }}>{item.unit || "—"}</td>
                  )}
                  {showUnitPrc && (
                    <td style={{ padding: "9px 10px", textAlign: "right", fontSize: "10px", fontFamily: "monospace", color: "#444" }}>
                      {item.unitPrice != null ? fc2(item.unitPrice) : "—"}
                    </td>
                  )}
                  <td style={{ padding: "9px 10px", textAlign: "right", fontSize: "11px", fontFamily: "monospace", fontWeight: 700, color: "#111" }}>
                    {fc2(item.amount)}
                  </td>
                  {showTaxCol && (
                    <td style={{ padding: "9px 10px", textAlign: "right", fontSize: "10px", fontFamily: "monospace", color: "#777" }}>
                      {N(item.taxAmount) > 0 ? fc2(item.taxAmount) : "—"}
                    </td>
                  )}
                </tr>
              ))}
              {/* Footer rows */}
              {(() => {
                const liTaxTotal = lineItems.reduce((s, i) => s + N(i.taxAmount), 0);
                const liGrossTotal = gross + liTaxTotal;
                const spanCols = [showNum, true, showQty, showUnit, showUnitPrc].filter(Boolean).length;
                return (
                  <>
                    {/* Net subtotal */}
                    <tr style={{ backgroundColor: "#f9fafb", borderTop: "2px solid #e5e7eb" }}>
                      <td colSpan={spanCols}
                        style={{ padding: "7px 12px", fontSize: "9px", fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "right" }}>
                        Net Sub-total
                      </td>
                      <td style={{ padding: "7px 10px", textAlign: "right", fontSize: "11px", fontFamily: "monospace", fontWeight: 700, color: "#555" }}>
                        {fc2(gross)}
                      </td>
                      {showTaxCol && <td style={{ padding: "7px 10px" }} />}
                    </tr>
                    {/* Tax row — only when tax exists */}
                    {liTaxTotal > 0 && (
                      <tr style={{ backgroundColor: "#f9fafb" }}>
                        <td colSpan={spanCols}
                          style={{ padding: "6px 12px", fontSize: "9px", fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "right" }}>
                          {taxLabel}
                        </td>
                        <td style={{ padding: "6px 10px", textAlign: "right", fontSize: "10px", fontFamily: "monospace", color: "#777" }}>
                          {fc2(liTaxTotal)}
                        </td>
                        {showTaxCol && (
                          <td style={{ padding: "6px 10px", textAlign: "right", fontSize: "10px", fontFamily: "monospace", color: "#777" }}>
                            {fc2(liTaxTotal)}
                          </td>
                        )}
                      </tr>
                    )}
                    {/* Gross total */}
                    <tr style={{ backgroundColor: "#f3f4f6", borderTop: "1px solid #e5e7eb" }}>
                      <td colSpan={spanCols}
                        style={{ padding: "9px 12px", fontSize: "10px", fontWeight: 900, color: "#333", textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "right" }}>
                        Gross Total
                      </td>
                      <td style={{ padding: "9px 10px", textAlign: "right", fontSize: "13px", fontFamily: "monospace", fontWeight: 900, color: "#111" }}>
                        {fc2(liGrossTotal)}
                      </td>
                      {showTaxCol && <td style={{ padding: "9px 10px" }} />}
                    </tr>
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>
      ) : !isReceipt ? (
        /* No line items — show a single amount row */
        <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: accentColor, color: "#fff" }}>
                <th style={thStyle("left")}>Description</th>
                <th style={thStyle("right")}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "14px 16px", fontSize: "11px", color: "#333" }}>{description || "—"}</td>
                <td style={{ padding: "14px 16px", textAlign: "right", fontSize: "13px", fontFamily: "monospace", fontWeight: 700, color: "#111" }}>{currency} {fc2(gross)}</td>
              </tr>
              <tr style={{ borderTop: "1px solid #e5e7eb", backgroundColor: "#f9fafb" }}>
                <td style={{ padding: "10px 16px", fontSize: "10px", color: "#888", fontWeight: 600, textTransform: "uppercase" as const }}>Subtotal</td>
                <td style={{ padding: "10px 16px", textAlign: "right", fontSize: "11px", fontFamily: "monospace", fontWeight: 700, color: "#555" }}>{currency} {fc2(gross)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );

  /* FINANCIAL SUMMARY */
  const S_totals = !isReceipt ? (
    <div style={{ padding: `24px ${wide ? "52px" : "48px"} 0`, display: "flex", justifyContent: "flex-end" }}>
      <div style={{ minWidth: wide ? "420px" : "340px", maxWidth: "520px" }}>
        <div style={{ border: "1px solid #e5e7eb", borderRadius: "10px", overflow: "hidden" }}>

          {/* Header bar */}
          <div style={{ backgroundColor: "#f9fafb", padding: "8px 16px", borderBottom: "1px solid #e5e7eb" }}>
            <span style={{ fontSize: "9px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: "#888" }}>Financial Summary</span>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>

              {/* Subtotal */}
              <SumRow label="Subtotal" value={`${currency} ${fc2(gross)}`} />

              {/* ── Pre-tax deductions ── */}
              {hasPre && (
                <tr>
                  <td colSpan={2} style={{ padding: "5px 16px 3px", fontSize: "8.5px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: "#dc2626", backgroundColor: "#fff5f5", borderTop: "1px solid #fee2e2", borderBottom: "1px solid #fee2e2" }}>
                    ↓ Deductions before tax
                  </td>
                </tr>
              )}

              {/* Invoice-level advance recovery → before tax */}
              {advAmt > 0 && (
                <SumRow label={`Advance Recovery (${(advRecPct * 100).toFixed(1)}%)`} value={`(${currency} ${fc2(advAmt)})`} negative />
              )}

              {/* Custom pre-tax deductions */}
              {preTaxDeds.map((d, i) => (
                <SumRow key={`pre-${i}`}
                  label={String(d.label || `Deduction ${i + 1}`)}
                  value={`(${currency} ${fc2(d.amount)})`}
                  negative
                />
              ))}

              {/* Taxable Base row (shown only when pre-tax deductions exist) */}
              {hasPre && (
                <>
                  <tr>
                    <td colSpan={2} style={{ padding: 0, height: "1px", backgroundColor: "#e5e7eb" }} />
                  </tr>
                  <SumRow label="Taxable Base" value={`${currency} ${fc2(taxableBase)}`} bold />
                  <tr>
                    <td colSpan={2} style={{ padding: 0, height: "1px", backgroundColor: "#e5e7eb" }} />
                  </tr>
                </>
              )}

              {/* Tax */}
              {vatAmt > 0 && (
                <SumRow label={`${taxLabel} (${vatPct}%)`} value={`${currency} ${fc2(vatAmt)}`} />
              )}

              {/* Gross incl. tax — only when post-tax deductions present */}
              {hasPost && (
                <>
                  <tr>
                    <td colSpan={2} style={{ padding: 0, height: "1px", backgroundColor: "#e5e7eb" }} />
                  </tr>
                  <SumRow label="Gross Total" value={`${currency} ${fc2(grossInclTax)}`} bold />
                  <tr>
                    <td colSpan={2} style={{ padding: "5px 16px 3px", fontSize: "8.5px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: "#dc2626", backgroundColor: "#fff5f5", borderBottom: "1px solid #fee2e2" }}>
                      ↓ Deductions after tax
                    </td>
                  </tr>
                </>
              )}

              {/* Retention */}
              {retAmt > 0 && (
                <SumRow label={`Retention (${(retPct * 100).toFixed(1)}%)`} value={`(${currency} ${fc2(retAmt)})`} negative />
              )}

              {/* Credit Note */}
              {cnAmt > 0 && (
                <SumRow label="Credit Note" value={`(${currency} ${fc2(cnAmt)})`} negative />
              )}

              {/* Custom post-tax deductions */}
              {postTaxDeds.map((d, i) => (
                <SumRow key={`post-${i}`}
                  label={String(d.label || `Deduction ${i + 1}`)}
                  value={`(${currency} ${fc2(d.amount)})`}
                  negative
                />
              ))}

              {/* Total Due — accent background */}
              <tr>
                <td colSpan={2} style={{ padding: 0, height: "2px", backgroundColor: "#e5e7eb" }} />
              </tr>
              <tr style={{ backgroundColor: accentColor }}>
                <td style={{ padding: "12px 16px", fontSize: "11px", fontWeight: 900, color: "#fff", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                  {hasDeductions ? "Net Amount Due" : "Total Due"}
                </td>
                <td style={{ padding: "12px 16px", textAlign: "right", fontSize: "15px", fontWeight: 900, fontFamily: "monospace", color: "#fff" }}>
                  {currency} {fc2(netDue)}
                </td>
              </tr>

            </tbody>
          </table>
        </div>
      </div>
    </div>
  ) : null;

  /* NOTES */
  const S_notes = notes ? (
    <div style={{ padding: `20px ${wide ? "52px" : "48px"} 0` }}>
      <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "16px" }}>
        <div style={{ fontSize: "8.5px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: "#888", marginBottom: "6px" }}>Notes & Terms</div>
        <div style={{ fontSize: "10px", color: "#555", whiteSpace: "pre-line", lineHeight: "1.7" }}>{notes}</div>
      </div>
    </div>
  ) : null;

  /* FOOTER */
  const S_footer = (
    <div style={{ padding: `20px ${wide ? "52px" : "48px"} 28px`, marginTop: "auto" }}>
      <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: "14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: "9px", color: "#ccc" }}>
          Generated by FinProject · {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
        </div>
        {company?.website && !isMinimal && (
          <div style={{ fontSize: "9px", color: "#ccc" }}>{company.website}</div>
        )}
      </div>
      {isBranded && <div style={{ height: "5px", backgroundColor: accentColor, marginTop: "14px", borderRadius: "0" }} />}
    </div>
  );

  const SECTIONS: Record<PrintSectionId, React.ReactNode> = {
    header:      S_header,
    party:       S_party,
    description: S_description,
    lineItems:   S_lineItems,
    totals:      S_totals,
    notes:       S_notes,
    footer:      S_footer,
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent
        className="w-full h-[92vh] p-0 flex flex-col rounded-2xl overflow-hidden print-dialog"
        style={{ maxWidth: dialogMaxW(customize), transition: "max-width 0.25s ease" }}
      >
        {/* Toolbar */}
        <div className="print-hide flex items-center gap-2 px-5 py-2.5 border-b border-border bg-muted/20 shrink-0">
          <FileText className="w-4 h-4 text-muted-foreground" />
          <span className="font-semibold text-sm">{config.label}</span>
          <span className="text-muted-foreground text-sm">· {refNumber}</span>
          <div className="ml-auto flex gap-1.5">
            <Button
              variant={wide ? "default" : "outline"}
              size="sm"
              className="rounded-xl gap-1.5 text-xs"
              onClick={toggleWide}
              title={wide ? "Switch to Portrait" : "Switch to Wide / Landscape"}
            >
              {wide ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
              {wide ? "Portrait" : "Wide"}
            </Button>
            <Button
              variant={customize ? "default" : "outline"}
              size="sm"
              className="rounded-xl gap-1.5 text-xs"
              onClick={() => setCustomize(c => !c)}
            >
              <Settings2 className="w-3.5 h-3.5" />
              {customize ? "Done" : "Customize"}
            </Button>
            <Button variant="outline" size="sm" className="rounded-xl gap-1.5 text-xs" onClick={() => window.print()}>
              <Printer className="w-3.5 h-3.5" /> Print / PDF
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-xl" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">

          {/* Document preview */}
          <div className="flex-1 overflow-auto bg-gray-100 dark:bg-neutral-900 p-6 print:p-0 print:overflow-visible print:bg-white">
            <div
              id="print-doc"
              style={{
                background: "#fff",
                color: "#111",
                width: `${DOC_W}px`,
                minHeight: `${DOC_H}px`,
                margin: "0 auto",
                fontFamily: "'Inter','Helvetica Neue',Arial,sans-serif",
                fontSize: "11px",
                boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
                borderRadius: "2px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Header — per-section font scale */}
              {sections.find(s => s.id === "header")?.visible && SECTIONS.header && (
                <div style={{ zoom: getSectionScale("header") }}>
                  <Fragment>{SECTIONS.header}</Fragment>
                </div>
              )}
              {/* Body sections — each with its own font scale */}
              <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                {sections
                  .filter(s => s.id !== "header")
                  .map(sec =>
                    sec.visible && SECTIONS[sec.id]
                      ? <div key={sec.id} style={{ zoom: getSectionScale(sec.id as PrintSectionId) }}><Fragment>{SECTIONS[sec.id]}</Fragment></div>
                      : null,
                  )}
              </div>
            </div>
          </div>

          {/* Customise panel */}
          {customize && (
            <div className="print-hide w-60 shrink-0 border-l border-border bg-background flex flex-col overflow-auto">

              {/* ── Color Scheme ── */}
              <PanelSection label="Color Scheme">
                <div className="grid grid-cols-3 gap-1.5">
                  {COLOR_SCHEMES.map(s => {
                    const bg = s.color ?? (company?.color || "#1e293b");
                    const active = colorScheme === s.id;
                    return (
                      <button
                        key={s.id}
                        onClick={() => saveColorScheme(s.id)}
                        title={s.label}
                        className={[
                          "flex flex-col items-center gap-1 p-1.5 rounded-lg border text-[10px] font-medium transition-all",
                          active ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border hover:border-primary/50",
                        ].join(" ")}
                      >
                        <span className="w-6 h-6 rounded-full border border-black/10 shrink-0" style={{ backgroundColor: bg }} />
                        <span className="text-muted-foreground">{s.label}</span>
                      </button>
                    );
                  })}
                </div>
              </PanelSection>

              {/* ── Logo ── */}
              {logoUrl && (
                <PanelSection label="Logo">
                  <button
                    onClick={toggleLogo}
                    className={[
                      "flex items-center gap-2 w-full px-2.5 py-2 rounded-lg border text-xs font-medium transition-all",
                      showLogo ? "border-primary bg-primary/5 text-primary" : "border-border bg-muted/30 text-muted-foreground opacity-60",
                    ].join(" ")}
                  >
                    {showLogo ? <Eye className="w-3.5 h-3.5 shrink-0" /> : <EyeOff className="w-3.5 h-3.5 shrink-0" />}
                    {showLogo ? "Logo visible" : "Logo hidden"}
                  </button>
                </PanelSection>
              )}

              {/* ── Line Item Columns ── */}
              {hasLineItems && (
                <PanelSection label="Item Columns" onReset={resetCols}>
                  {(
                    [
                      { key: "num"      as ColKey, label: "# (row number)" },
                      { key: "qty"      as ColKey, label: "Qty" },
                      { key: "unit"     as ColKey, label: "Unit" },
                      { key: "unitPrice"as ColKey, label: "Unit Price" },
                      { key: "tax"      as ColKey, label: "Tax" },
                    ] as { key: ColKey; label: string }[]
                  ).map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => toggleCol(key)}
                      className={[
                        "flex items-center gap-2 w-full px-2.5 py-2 rounded-lg border text-xs font-medium transition-all",
                        cols[key] ? "border-border bg-muted/30 hover:bg-muted/50" : "border-border bg-muted/30 opacity-40",
                      ].join(" ")}
                    >
                      {cols[key] ? <Eye className="w-3.5 h-3.5 shrink-0 text-muted-foreground" /> : <EyeOff className="w-3.5 h-3.5 shrink-0 opacity-40" />}
                      {label}
                    </button>
                  ))}
                </PanelSection>
              )}

              {/* ── Sections (with per-section font size) ── */}
              <PanelSection label="Sections" onReset={resetLayout}>
                {sections.map(sec => {
                  const activePreset = sectionFonts[sec.id] ?? "md";
                  return (
                    <div
                      key={sec.id}
                      draggable
                      onDragStart={e => { e.dataTransfer.setData("text/plain", sec.id); e.dataTransfer.effectAllowed = "move"; }}
                      onDragOver={e => handleDragOver(e, sec.id)}
                      onDrop={e => handleDrop(e, sec.id)}
                      onDragLeave={() => setDragOver(null)}
                      onDragEnd={() => setDragOver(null)}
                      className={[
                        "rounded-lg border select-none transition-all",
                        "cursor-grab active:cursor-grabbing",
                        dragOver === sec.id
                          ? "border-primary bg-primary/5 shadow-sm"
                          : "border-border bg-muted/30 hover:bg-muted/50",
                        !sec.visible ? "opacity-40" : "",
                      ].join(" ")}
                    >
                      {/* Row 1: grip + label + eye */}
                      <div className="flex items-center gap-2 px-2.5 py-1.5">
                        <GripVertical className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0" />
                        <span className="flex-1 text-xs font-medium">{sec.label}</span>
                        <button
                          onMouseDown={e => e.stopPropagation()}
                          onClick={e => { e.stopPropagation(); toggleVisible(sec.id); }}
                          className="text-muted-foreground hover:text-foreground transition-colors shrink-0 rounded p-0.5"
                          title={sec.visible ? "Hide" : "Show"}
                        >
                          {sec.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5 opacity-40" />}
                        </button>
                      </div>
                      {/* Row 2: font size presets */}
                      <div
                        className="flex gap-0.5 px-2.5 pb-1.5"
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => e.stopPropagation()}
                      >
                        {FONT_PRESETS.map(p => (
                          <button
                            key={p.id}
                            onMouseDown={e => e.stopPropagation()}
                            onClick={e => { e.stopPropagation(); saveSectionFont(sec.id as PrintSectionId, p.id); }}
                            className={[
                              "flex-1 py-0.5 rounded border text-[9px] font-semibold transition-all",
                              activePreset === p.id
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-border bg-background text-muted-foreground hover:border-primary/50",
                            ].join(" ")}
                          >
                            {p.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </PanelSection>

              <div className="px-3 py-3 border-t border-border mt-auto">
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  All settings saved per company
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Panel section helper ───────────────────────────────────────────────────────

function PanelSection({ label, children, onReset }: { label: string; children: React.ReactNode; onReset?: () => void }) {
  return (
    <div className="border-b border-border">
      <div className="flex items-center justify-between px-4 py-2.5 bg-muted/20">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</span>
        {onReset && (
          <button onClick={onReset} className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors">
            <RotateCcw className="w-2.5 h-2.5" /> Reset
          </button>
        )}
      </div>
      <div className="p-3 space-y-1.5">
        {children}
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function thStyle(align: "left" | "center" | "right"): React.CSSProperties {
  return {
    padding: "10px 10px",
    textAlign: align,
    fontSize: "9px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    color: "rgba(255,255,255,0.9)",
  };
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <tr>
      <td style={{ padding: "2px 8px 2px 0", fontSize: "8.5px", color: "#aaa", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.3px", whiteSpace: "nowrap" }}>
        {label}
      </td>
      <td style={{ padding: "2px 0", fontSize: "10px", color: "#333", fontWeight: 600 }}>{value}</td>
    </tr>
  );
}

function SumRow({ label, value, negative, bold }: { label: string; value: string; negative?: boolean; bold?: boolean }) {
  return (
    <tr style={{ borderBottom: "1px solid #f5f5f5" }}>
      <td style={{
        padding: "7px 16px",
        fontSize: "10px",
        color: negative ? "#dc2626" : bold ? "#111" : "#555",
        fontWeight: bold ? 700 : 400,
      }}>
        {label}
      </td>
      <td style={{
        padding: "7px 16px",
        textAlign: "right",
        fontSize: bold ? "11px" : "10px",
        fontFamily: "monospace",
        color: negative ? "#dc2626" : bold ? "#111" : "#333",
        fontWeight: bold ? 700 : 400,
      }}>
        {value}
      </td>
    </tr>
  );
}
